import { Component, Input, OnInit } from '@angular/core';
import { AgencyService, ClientService } from 'app/_services';
import {ConfirmationService, MessageService} from 'primeng/api';
import {TranslateService} from '@ngx-translate/core';
import { AITranslationService } from 'app/_services/ai-translation.service';

@Component({
  selector: 'app-edit-clothing-requirements',
  templateUrl: './edit-clothing-requirements.component.html',
  styleUrls: ['./edit-clothing-requirements.component.scss'],
  providers: [MessageService, ConfirmationService]
})
export class EditClothingRequirementsComponent implements OnInit {
  // JS-1920 (2026-10-20): when set, the component manages the given client's clothing
  // requirements via /api/client/{id}/clothing-requirements. When unset, the original
  // agency-scoped flow runs unchanged. This lets the same component serve both the standalone
  // agency route (/agency/edit-clothing-requirements) and the client Portal Settings popup.
  @Input() clientId?: number;

  showDialog = false;
  selectedCategory = '';
  isTitleNotFilled = false;
  isEditing = false;
  editIndex: number | null = null;
  agencyId: number | null = null;

  savedItems = {
    shirt: [] as { id: number | null, title: { en: string; de: string; fr: string; it: string } }[],
    trousers: [] as { id: number | null, title: { en: string; de: string; fr: string; it: string } }[],
    shoes: [] as { id: number | null, title: { en: string; de: string; fr: string; it: string } }[],
    accessories: [] as { id: number | null, title: { en: string; de: string; fr: string; it: string } }[],
  };

  public languages = [ 
    { value: 'en', label: 'English', labelKey: 'English' },
    { value: 'de', label: 'Deutsch', labelKey: 'German' },
    { value: 'fr', label: 'Français', labelKey: 'French' },
    { value: 'it', label: 'Italiano', labelKey: 'Italian' }
  ];

  public currentLanguage = localStorage.getItem('Language');
  public activeLangTabIndex: number = 0;
  public isTranslating = false;
  fieldTranslations = {
    title: { en: '', de: '', fr: '', it: '' }
  };

  constructor(
    private agencyService: AgencyService,
    private messageService: MessageService,
    private translateService: TranslateService,
    private confirmationService: ConfirmationService,
    private aiTranslationService: AITranslationService,
    // JS-1920 (2026-10-20): injected for the client-scoped CRUD branch.
    private clientService: ClientService,
  ) {}

  ngOnInit(): void {
    this.activeLangTabIndex = this.selectedTab() || 0;
    let user = JSON.parse(localStorage.getItem('currentUser'));
    this.agencyId = user.agency_id;
    // JS-1920 (2026-10-20): branch on [clientId]. Client-scoped load when provided; otherwise
    // the original agency-wide load.
    const loader$ = this.clientId
      ? this.clientService.getClientClothingRequirements(this.clientId)
      : this.agencyService.getClothingRequirements();
    loader$.subscribe((response: { requirements }) => {
      this.savedItems = response.requirements;
    });
  }

  openDialog(category: string) {
    this.selectedCategory = category;
    this.isTitleNotFilled = false;
    this.showDialog = true;
  }

  selectedTab(): number {
    if (this.currentLanguage === 'en') return 0;
    if (this.currentLanguage === 'de') return 1;
    if (this.currentLanguage === 'fr') return 2;
    if (this.currentLanguage === 'it') return 3;
    return 0;
  }

  saveNewOption() {
    const titles = this.fieldTranslations.title;
    const atLeastOneFilled = ['en', 'de', 'fr', 'it'].some(
      lang => titles[lang]?.trim()
    );

    if (!atLeastOneFilled) {
      this.isTitleNotFilled = true;
      return;
    }

    this.isTitleNotFilled = false;

    if (this.isEditing && this.editIndex !== null) {
      const existingId = this.savedItems[this.selectedCategory][this.editIndex].id;
      if (existingId) {
        // JS-1920 (2026-10-20): client-scoped update when [clientId] is set.
        const update$ = this.clientId
          ? this.clientService.updateClientClothingRequirement(this.clientId, existingId, {title: titles})
          : this.agencyService.updateClothingRequirement(existingId, {title: titles});
        update$.subscribe(response => {
          this.savedItems[this.selectedCategory][this.editIndex] = {
            id: existingId,
            title: { ...titles }
          };
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('Success!'),
            detail: this.translateService.instant('Clothing requirement updated successfully.')
          });
          this.cancelDialog();
        });
      }
    } else {
      // JS-1920 (2026-10-20): client-scoped create when [clientId] is set.
      const create$ = this.clientId
        ? this.clientService.saveClientClothingRequirement(this.clientId, {type: this.selectedCategory, title: titles})
        : this.agencyService.saveNewClothingRequirement({type: this.selectedCategory, title: titles});
      create$.subscribe((response: { requirement: { id: number } }) => {
        this.savedItems[this.selectedCategory].push({
          id: response.requirement.id,
          title: { ...titles }
        });
        this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('Success!'),
            detail: this.translateService.instant('Clothing requirement saved successfully.')
          });
        this.cancelDialog();
      });
    }
  }

  hasOtherLangContent(targetLang: string): boolean {
    const langs = ['en', 'de', 'fr', 'it'];
    return langs.some(l => l !== targetLang && !!(this.fieldTranslations.title[l] && this.fieldTranslations.title[l].trim()));
  }

  translateToLang(targetLang: string) {
    const langs = ['en', 'de', 'fr', 'it'];
    let sourceText = '';
    let sourceLang = '';
    for (const lang of langs) {
      if (lang !== targetLang && this.fieldTranslations.title[lang]?.trim()) {
        sourceText = this.fieldTranslations.title[lang];
        sourceLang = lang;
        break;
      }
    }

    this.isTranslating = true;
    this.aiTranslationService.translate(sourceText, sourceLang, targetLang).subscribe(
      response => {
        this.fieldTranslations.title[targetLang] = response.translated_text;
        this.isTranslating = false;
      },
      () => { this.isTranslating = false; }
    );
  }

  cancelDialog() {
    this.showDialog = false;
    this.isEditing = false;
    this.fieldTranslations.title = { en: '', de: '', fr: '', it: ''};
    this.isTitleNotFilled = false;
    this.selectedCategory = '';
  }

  deleteItem(category: string, index: number, id: number | null = null) {

    this.confirmationService.confirm({
      header: this.translateService.instant("Confirmation"),
      message: this.translateService.instant("Are you sure you want to delete this?"),
      accept: () => {
        // JS-1920 (2026-10-20): client-scoped delete when [clientId] is set.
        const delete$ = this.clientId
          ? this.clientService.deleteClientClothingRequirement(this.clientId, id)
          : this.agencyService.deleteClothingRequirement(id);
        delete$.subscribe(response => {
          this.savedItems[category].splice(index, 1);
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('Success!'),
            detail: this.translateService.instant('Clothing requirement deleted successfully.')
          });
        });
      }
    });
  }

  editItem(category: string, index: number) {
    const itemToEdit = this.savedItems[category][index];

    if (!itemToEdit || !itemToEdit.title) return;

    this.fieldTranslations.title = { ...itemToEdit.title };
    this.selectedCategory = category;
    this.editIndex = index;
    this.isEditing = true;
    this.showDialog = true;
  }
}
